import { computed, type Ref } from "vue";
import { useField, useForm } from "vee-validate";
import { boolean as yupBoolean, string as yupString } from "yup";
import { useI18n } from "vue-i18n";
import { RegistrationType } from "@/shared/generated/graphql";
import { useEmailField } from "@/shared/composables/useEmailField.composable";
import { useRegistrationSalutationField } from "@/shared/composables/useRegistrationSalutationField.composable";
import { useFirstNameField } from "@/shared/composables/useFirstNameField.composable";
import { useLastNameField } from "@/shared/composables/useLastNameField.composable";
import type { RegistrationFormData } from "@/app/registration-app/user/invited-registration/@types/RegistrationFormData";
import type { InitialRegistrationData } from "@/app/registration-app/user/invited-registration/@types/InitialRegistrationData";

export const useRegistrationDataFields = (
  registrationType: Ref<RegistrationType>,
  finishedRegistration: boolean,
) => {
  const { t } = useI18n();

  const { handleSubmit } = useForm<RegistrationFormData>();

  const { salutation, salutationOptions } = useRegistrationSalutationField({
    required: true,
  });
  const { firstName } = useFirstNameField();

  const { lastName } = useLastNameField();

  const { email } = useEmailField();

  const passwordValidation = computed(() =>
    registrationType.value === RegistrationType.Email
      ? yupString().min(10, possiblePasswordErrors[0]).required()
      : undefined,
  );

  const possiblePasswordErrors = [
    t("shared.form.validation.min", { count: 10 }),
  ];
  const password = useField<string>("password", passwordValidation, {
    initialValue: "",
  });

  const conditions = useField<boolean>(
    "conditions",
    yupBoolean()
      .required(t("shared.form.validation.checked"))
      .oneOf([true], t("shared.form.validation.checked")),
    { initialValue: finishedRegistration },
  );

  const privacy = useField<boolean>(
    "privacy",
    yupBoolean()
      .required(t("shared.form.validation.checked"))
      .oneOf([true], t("shared.form.validation.checked")),
    { initialValue: finishedRegistration },
  );

  const newsletter = useField<boolean>("newsletter", yupBoolean(), {
    initialValue: false,
  });

  const setInitialRegistrationData = (
    initialValues: InitialRegistrationData,
  ) => {
    email.value.value = initialValues.email ?? "";
    if (initialValues.salutation)
      salutation.value.value = initialValues.salutation;
    if (initialValues.firstName)
      firstName.value.value = initialValues.firstName;
    if (initialValues.lastName) lastName.value.value = initialValues.lastName;
  };

  return {
    salutation,
    salutationOptions,
    firstName,
    lastName,
    password,
    possiblePasswordErrors,
    email,
    conditions,
    privacy,
    newsletter,
    handleSubmit,
    setInitialRegistrationData,
  };
};
